import { cn } from "@/lib/utils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import SdiErrorMessage from "./SdiErrorMessage";
import { labelDestinatarioOspite } from "../utils/fatturazioneLabels";
import {
  Loader2,
  RefreshCw,
  Filter,
  Download,
  Send,
  Eye,
  Search,
  RotateCcw,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Euro,
  FileCheck,
  MoreVertical,
  Bell,
  AlertTriangle,
  X,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { SDI_STATO_BADGE, SDI_FILTRO_STATO, sdiEsitoLabel, sdiDataConsegna, sdiStatoGruppo, isFicUsageLimitSyncError, ficEiStatusLabel } from "@/utils/fatturazioneSdi";
import { FIC_RATE_LIMIT_MESSAGE, retryIsActive, retryLabel } from "@/utils/ficSyncStato";

const SDI_STATO_ICONS = {
  consegnata: CheckCircle2,
  accettata: CheckCircle2,
  inviata_sdi: Clock,
  scartata: XCircle,
  rifiutata: XCircle,
};

const ACTION_BTN =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-50";

function SdiKpiCard({ icon: Icon, iconClass, title, value, sub, pct, barClass }) {
  return (
    <Card className="card-base border-slate-200/80 shadow-sm">
      <CardContent className="p-4 flex gap-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", iconClass)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-600">{title}</p>
          <p className="text-xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          {pct != null && pct > 0 && (
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full transition-all", barClass)} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatoSdiBadge({ stato }) {
  const cfg = SDI_STATO_BADGE[stato] || { label: stato, className: "bg-slate-100 text-slate-700" };
  const Icon = SDI_STATO_ICONS[stato] || Clock;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        cfg.className,
      )}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
      {cfg.label}
    </span>
  );
}

function PaginationBar({ page, lastPage, total, rowsPerPage, onPageChange, onRowsPerPageChange }) {
  const from = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
      <span>{total > 0 ? `${from}-${to} di ${total} risultati` : "Nessun risultato"}</span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <span className="sr-only">Precedente</span>‹
          </Button>
          <span className="px-2 tabular-nums min-w-[4rem] text-center">
            {page} / {lastPage}
          </span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page >= lastPage} onClick={() => onPageChange(page + 1)}>
            <span className="sr-only">Successiva</span>›
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Righe per pagina</span>
          <Select value={String(rowsPerPage)} onValueChange={(v) => onRowsPerPageChange(Number(v))}>
            <SelectTrigger className="h-8 w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 25, 50].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function FatturaSdiAzioni({
  f,
  openDetail,
  handleOpenFatturaPdf,
  handleDownloadXml,
  handleInviaSdi,
  sendingSdi,
  canInviaSdi,
  onSollecitaPagamento,
}) {
  const canResend = sdiStatoGruppo(f.stato) === "scartata";
  const showSollecito = f.stato_pagamento !== "pagata";

  return (
    <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button type="button" className={ACTION_BTN} title="Visualizza fattura" onClick={() => openDetail(f.id)}>
        <Eye className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={ACTION_BTN}
        title="PDF"
        onClick={() => handleOpenFatturaPdf(f)}
        disabled={!(f.voci?.length > 0)}
      >
        <FileText className="w-4 h-4" />
      </button>
      <button type="button" className={ACTION_BTN} title="XML" onClick={() => handleDownloadXml(f.id, f.numero)}>
        <Download className="w-4 h-4" />
      </button>
      {canResend && (
        <button
          type="button"
          className={cn(ACTION_BTN, "text-emerald-600 hover:bg-emerald-50/80")}
          title={canInviaSdi ? "Reinvia a SDI" : "Configura la fatturazione in Impostazioni"}
          disabled={sendingSdi || !canInviaSdi}
          onClick={() => handleInviaSdi(f.id)}
        >
          {sendingSdi ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={ACTION_BTN} title="Altre azioni">
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => openDetail(f.id)}>Dettaglio fattura</DropdownMenuItem>
          {f.voci?.length > 0 && (
            <DropdownMenuItem onClick={() => handleOpenFatturaPdf(f)}>Apri PDF</DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => handleDownloadXml(f.id, f.numero)}>Scarica XML</DropdownMenuItem>
          {canResend && (
            <DropdownMenuItem disabled={!canInviaSdi || sendingSdi} onClick={() => handleInviaSdi(f.id)}>
              Reinvia a SDI
            </DropdownMenuItem>
          )}
          {showSollecito && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onSollecitaPagamento(f)}>
                <Bell className="w-3.5 h-3.5 mr-2 text-amber-600" />
                Sollecita pagamento
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function FatturazioneSdiTab({
  currentYear,
  formatEuro,
  formatDataIt,
  formatDataOraIt,
  hasProviderConfig,
  isFic,
  isAruba,
  sdiStats,
  sdiSearchQuery,
  setSdiSearchQuery,
  sdiFiltroStato,
  setSdiFiltroStato,
  sdiFiltroMese,
  setSdiFiltroMese,
  sdiDataDa,
  setSdiDataDa,
  sdiDataA,
  setSdiDataA,
  sdiMesiOptions,
  formatMese,
  clearSdiFilters,
  handleSincronizzaSdi,
  sdiSyncLoading,
  sdiUsageLimitBanner,
  dismissSdiUsageLimitBanner,
  loadingFatture,
  fattureSdiFiltrate,
  fattureSdiPaginate,
  sdiPage,
  setSdiPage,
  sdiLastPage,
  sdiRowsPerPage,
  setSdiRowsPerPage,
  openDetail,
  handleOpenFatturaPdf,
  handleDownloadXml,
  handleInviaSdi,
  sendingSdi,
  canInviaSdi,
  onSollecitaPagamento,
  fascicoloLabel,
}) {
  const ficUrl = "https://secure.fattureincloud.it";
  const retryActive = retryIsActive(sdiUsageLimitBanner);
  const retryAt = retryLabel(sdiUsageLimitBanner, formatDataOraIt);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Fatture inviate allo SDI</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Esiti di consegna e scarti SDI. «Inviata / in consegna» significa già trasmessa (come «Inviato» su FIC),
            in attesa di consegna al destinatario.
          </p>
        </div>
        {hasProviderConfig && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={handleSincronizzaSdi}
            disabled={sdiSyncLoading || loadingFatture}
          >
            {sdiSyncLoading || loadingFatture ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {isFic ? "Sincronizza da Fatture in Cloud" : isAruba ? "Sincronizza da Aruba" : "Sincronizza stato SDI"}
          </Button>
        )}
      </div>

      {sdiUsageLimitBanner?.message && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-700">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="pr-8">Quota API Fatture in Cloud esaurita</AlertTitle>
          <AlertDescription className="text-amber-900/90">
            <p>{FIC_RATE_LIMIT_MESSAGE}</p>
            <p className="mt-1.5 text-xs text-amber-800/80">
              Non è uno scarto SDI: le fatture restano «Inviata / in consegna» finché non potrai sincronizzare di nuovo.
              {sdiUsageLimitBanner.ultimo_aggiornamento_riuscito ? <> Ultimo aggiornamento riuscito: {formatDataOraIt?.(sdiUsageLimitBanner.ultimo_aggiornamento_riuscito) || sdiUsageLimitBanner.ultimo_aggiornamento_riuscito}.</> : null}
              {sdiUsageLimitBanner.ultimo_stato_sdi ? <> Ultimo stato SDI disponibile: {ficEiStatusLabel(sdiUsageLimitBanner.ultimo_stato_sdi) || sdiUsageLimitBanner.ultimo_stato_sdi}.</> : null}
              {retryAt ? <> Prossimo tentativo programmato: {retryAt}.</> : null}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                onClick={handleSincronizzaSdi}
                disabled={sdiSyncLoading || loadingFatture || retryActive}
              >
                {sdiSyncLoading || loadingFatture ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                )}
                {retryActive ? `Nuovo tentativo alle ${retryAt}` : "Riprova aggiornamento"}
              </Button>
              {typeof dismissSdiUsageLimitBanner === "function" && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 text-amber-800 hover:bg-amber-100 hover:text-amber-950"
                  onClick={dismissSdiUsageLimitBanner}
                >
                  Nascondi
                </Button>
              )}
            </div>
          </AlertDescription>
          {typeof dismissSdiUsageLimitBanner === "function" && (
            <button
              type="button"
              className="absolute right-3 top-3 rounded-md p-1 text-amber-700/70 hover:bg-amber-100 hover:text-amber-900"
              onClick={dismissSdiUsageLimitBanner}
              aria-label="Chiudi avviso quota"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </Alert>
      )}

      {!hasProviderConfig ? (
        <Card className="card-base">
          <CardContent className="p-6 text-sm text-slate-500">
            Configura Fatture in Cloud o Aruba in Impostazioni per inviare fatture allo SDI e monitorarne gli esiti.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <SdiKpiCard
              icon={FileCheck}
              iconClass="bg-blue-100 text-blue-700"
              title="Inviate"
              value={sdiStats.totale}
              sub={sdiStats.totale ? "100% del totale" : undefined}
            />
            <SdiKpiCard
              icon={CheckCircle2}
              iconClass="bg-emerald-100 text-emerald-700"
              title="Consegnate"
              value={sdiStats.consegnate.count}
              sub={`${sdiStats.consegnate.pct}% del totale`}
              pct={sdiStats.consegnate.pct}
              barClass="bg-emerald-500"
            />
            <SdiKpiCard
              icon={XCircle}
              iconClass="bg-red-100 text-red-700"
              title="Scartate"
              value={sdiStats.scartate.count}
              sub={`${sdiStats.scartate.pct}% del totale`}
              pct={sdiStats.scartate.pct}
              barClass="bg-red-500"
            />
            <SdiKpiCard
              icon={Clock}
              iconClass="bg-amber-100 text-amber-700"
              title="In consegna"
              value={sdiStats.inAttesa.count}
              sub={`${sdiStats.inAttesa.pct}% del totale`}
              pct={sdiStats.inAttesa.pct}
              barClass="bg-amber-500"
            />
            <SdiKpiCard
              icon={Clock}
              iconClass="bg-violet-100 text-violet-700"
              title="Esito da verificare"
              value={sdiStats.daVerificare?.count ?? 0}
              sub={`${sdiStats.daVerificare?.pct ?? 0}% del totale`}
              pct={sdiStats.daVerificare?.pct}
              barClass="bg-violet-500"
            />
          </div>

          <Card className="card-base overflow-hidden">
            <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    className="pl-9 bg-white"
                    placeholder="Cerca numero, destinatario…"
                    value={sdiSearchQuery}
                    onChange={(e) => setSdiSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={sdiFiltroStato} onValueChange={setSdiFiltroStato}>
                    <SelectTrigger className="w-[180px] bg-white h-9 text-sm">
                      <SelectValue placeholder="Tutti gli stati" />
                    </SelectTrigger>
                    <SelectContent>
                      {SDI_FILTRO_STATO.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={sdiFiltroMese} onValueChange={setSdiFiltroMese}>
                    <SelectTrigger className="w-[150px] bg-white h-9 text-sm">
                      <SelectValue placeholder="Tutti i mesi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__tutti__">Tutti i mesi</SelectItem>
                      {sdiMesiOptions.map((m) => (
                        <SelectItem key={m} value={m}>
                          {formatMese(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    className="w-[130px] h-9 bg-white text-sm"
                    value={sdiDataDa}
                    onChange={(e) => setSdiDataDa(e.target.value)}
                    title="Da data"
                    aria-label="Da data"
                  />
                  <Input
                    type="date"
                    className="w-[130px] h-9 bg-white text-sm"
                    value={sdiDataA}
                    onChange={(e) => setSdiDataA(e.target.value)}
                    title="A data"
                    aria-label="A data"
                  />
                  <Button type="button" variant="outline" size="sm" className="h-9" disabled={loadingFatture}>
                    <Filter className="w-3.5 h-3.5 mr-1.5" />
                    Filtri
                  </Button>
                  <button
                    type="button"
                    className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                    onClick={clearSdiFilters}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Pulisci
                  </button>
                </div>
              </div>
            </div>

            {loadingFatture && fattureSdiFiltrate.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                Caricamento…
              </div>
            ) : fattureSdiFiltrate.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">Nessuna fattura inviata allo SDI con i filtri selezionati.</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-slate-500 font-medium">Numero fattura</TableHead>
                      <TableHead className="text-slate-500 font-medium">Data invio</TableHead>
                      <TableHead className="text-slate-500 font-medium">Destinatario</TableHead>
                      <TableHead className="text-slate-500 font-medium text-right">Importo</TableHead>
                      <TableHead className="text-slate-500 font-medium">Stato SDI</TableHead>
                      <TableHead className="text-slate-500 font-medium">Data consegna</TableHead>
                      <TableHead className="text-slate-500 font-medium">Esito</TableHead>
                      <TableHead className="text-slate-500 font-medium text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fattureSdiPaginate.map((f) => {
                      const esito = sdiEsitoLabel(f);
                      const esitoScarto = sdiStatoGruppo(f.stato) === "scartata";
                      const dataConsegna = sdiDataConsegna(f);
                      const label = labelDestinatarioOspite({
                        destinatarioNome: f.destinatario_nome,
                        ospiteNome: fascicoloLabel?.(f.fascicolo_id) || "",
                      });
                      return (
                        <TableRow
                          key={f.id}
                          className="cursor-pointer hover:bg-slate-50/80"
                          onClick={() => openDetail(f.id)}
                        >
                          <TableCell>
                            <span className="font-mono text-sm text-emerald-700">{f.numero}</span>
                          </TableCell>
                          <TableCell className="text-slate-700 whitespace-nowrap">
                            {f.sdi_invio_at ? formatDataOraIt(f.sdi_invio_at) : formatDataIt(f.data_emissione)}
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            <p className="font-medium text-slate-800 truncate" title={label.full}>
                              {label.primary}
                            </p>
                            {label.secondary && (
                              <p className="text-[11px] text-slate-500 truncate">({label.secondary})</p>
                            )}
                            {(f.destinatario_piva || f.destinatario_cf) && (
                              <p className="text-[11px] text-slate-500 truncate">
                                {f.destinatario_piva ? `P.IVA ${f.destinatario_piva}` : ""}
                                {f.destinatario_piva && f.destinatario_cf ? " · " : ""}
                                {f.destinatario_cf ? `C.F. ${f.destinatario_cf}` : ""}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{formatEuro(f.totale)}</TableCell>
                          <TableCell>
                            <div className="space-y-0.5">
                              <StatoSdiBadge stato={f.stato} />
                              {f.stato === "inviata_sdi" && ficEiStatusLabel(f.sdi_ei_status) && (
                                <p className="text-[11px] text-slate-500 pl-0.5">
                                  FIC: {ficEiStatusLabel(f.sdi_ei_status)}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700 whitespace-nowrap">
                            <div>
                              {dataConsegna ? formatDataOraIt(dataConsegna) : "—"}
                              {esitoScarto && f.ultimo_errore_sdi && (
                                <p className="text-[11px] text-red-600 font-medium mt-0.5">
                                  {(() => {
                                    const raw = String(f.ultimo_errore_sdi_leggibile || f.ultimo_errore_sdi || "");
                                    const m = raw.match(/(?:cod\.?|codice)\s*([0-9A-Za-z]+)/i);
                                    return m ? `Cod. ${m[1]}` : raw.slice(0, 40);
                                  })()}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            {esitoScarto && f.ultimo_errore_sdi ? (
                              <SdiErrorMessage
                                error={f.ultimo_errore_sdi}
                                userMessage={f.ultimo_errore_sdi_leggibile}
                                variant="inline"
                              />
                            ) : f.sdi_ultimo_sync_errore &&
                              sdiStatoGruppo(f.stato) === "in_attesa" &&
                              !isFicUsageLimitSyncError(f.sdi_ultimo_sync_errore) ? (
                              <span className="text-xs text-amber-700" title={f.sdi_ultimo_sync_errore}>
                                {esito}
                              </span>
                            ) : (
                              <span className={cn("text-xs", esitoScarto ? "text-red-600" : "text-slate-600")}>{esito}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <FatturaSdiAzioni
                              f={f}
                              openDetail={openDetail}
                              handleOpenFatturaPdf={handleOpenFatturaPdf}
                              handleDownloadXml={handleDownloadXml}
                              handleInviaSdi={handleInviaSdi}
                              sendingSdi={sendingSdi}
                              canInviaSdi={canInviaSdi}
                              onSollecitaPagamento={onSollecitaPagamento}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <PaginationBar
                  page={sdiPage}
                  lastPage={sdiLastPage}
                  total={fattureSdiFiltrate.length}
                  rowsPerPage={sdiRowsPerPage}
                  onPageChange={setSdiPage}
                  onRowsPerPageChange={setSdiRowsPerPage}
                />
              </>
            )}
          </Card>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-3 text-sm text-sky-900">
            <p className="text-sky-800/90">
              I dati vengono sincronizzati automaticamente da{" "}
              {isFic ? "Fatture in Cloud" : isAruba ? "Aruba" : "il provider configurato"} ogni 30 minuti.
              Usa <strong>Sincronizza</strong> per aggiornare subito le fatture ancora in consegna.
            </p>
            {isFic && (
              <Button type="button" variant="outline" size="sm" className="shrink-0 border-sky-200 bg-white" asChild>
                <a href={ficUrl} target="_blank" rel="noopener noreferrer">
                  Vai a Fatture in Cloud
                </a>
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
